import { PriceRoundingMethod } from "../codegen/graphql";
import { SupplierName } from "./suppliersConfig";
import memoizee from 'memoizee';

export interface ExternalListingConfiguration {
  isMultiVariation: boolean | null;
  useSingleVariationId: string | null;
  supplierKey: SupplierName | null;

  marginPercentOnBaseCost: number | null;
  targetListingPriceIncludingVat: number | null;
  listingPriceRoundingMethod: PriceRoundingMethod | null;
  
  variationsConfig: VariationsConfiguration;
}

export interface VariationConfigurationType {
  useSingleValueId: string | null;
  extraData: any;
}

export interface VariationConfigurationValue {
  include: boolean;
  marginPercentOnBaseCost: number | null;
  targetListingPriceIncludingVat: number | null;
  listingPriceRoundingMethod: PriceRoundingMethod | null;
  extraData: any;
}

export interface VariationConfigurationVariation {
  include: boolean;
  marginPercentOnBaseCost: number | null;
  targetListingPriceIncludingVat: number | null;
  listingPriceRoundingMethod: PriceRoundingMethod | null;
  externalId?: string | null;
  extraData: any;
}

export interface VariationsConfiguration {
  types: { [typeId: string]: VariationConfigurationType };
  values: { [typeValueId: string]: VariationConfigurationValue };
  variations: { [variationId: string]: VariationConfigurationVariation };
}

interface VariationRequiredData {
  id: string;
  variationTypeValues: {
    id: string;
    value: string;
    type: {
      id: string;
      type: string;
    };
  }[];
}

interface VariationTypeRequiredData {
  id: string;
  type: string;
  typeValues: {
    id: string;
    value: string;
  }[];
}

interface VariationTypeValueRequiredData {
  id: string;
  value: string;
}

// Create a default variations configuration with all types, values and variations included
export function createDefaultVariationsConfiguration<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>(
  types: T[],
  variations: V[],
) {
  const config: VariationsConfiguration = {
    types: {},
    values: {},
    variations: {},
  };

  types.forEach(type => {
    config.types[type.id] = {
      useSingleValueId: null,
      extraData: {},
    };

    type.typeValues.forEach(value => {
      config.values[value.id] = {
        include: true,
        marginPercentOnBaseCost: null,
        targetListingPriceIncludingVat: null,
        listingPriceRoundingMethod: null,
        extraData: {},
      };
    });
  });

  variations.forEach(variation => {
    config.variations[variation.id] = {
      include: true,
      marginPercentOnBaseCost: null,
      targetListingPriceIncludingVat: null,
      listingPriceRoundingMethod: null,
      extraData: {},
    };
  });

  return config;
}

export const createListingConfigurationWithDefaults = memoizee(function createListingConfigurationWithDefaults<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>(
  existingConfig: Partial<ExternalListingConfiguration> | undefined | null,
  types: T[],
  variations: V[],
) {
  return {
    isMultiVariation: null,
    useSingleVariationId: null,
    supplierKey: null,
    marginPercentOnBaseCost: null,
    targetListingPriceIncludingVat: null,
    listingPriceRoundingMethod: null,
    ...existingConfig,
    variationsConfig: mergeVariationsConfiguration(existingConfig?.variationsConfig, types, variations),
  };
})

// Merge an existing configuration with a default one,
// And removes any type, value or variation config that could have been removed
export function mergeVariationsConfiguration<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>(
  existingConfig: VariationsConfiguration | undefined | null,
  types: T[],
  variations: V[],
) {
  const config: VariationsConfiguration = {
    types: {},
    values: {},
    variations: {},
  };

  types.forEach(type => {
    if (existingConfig?.types[type.id]) {
      config.types[type.id] = existingConfig.types[type.id];
    } else {
      config.types[type.id] = {
        useSingleValueId: null,
        extraData: {},
      };
    };

    type.typeValues.forEach(value => {
      if (existingConfig?.values[value.id]) {
        config.values[value.id] = existingConfig.values[value.id];
      } else {
        config.values[value.id] = {
          include: true,
          marginPercentOnBaseCost: null,
          targetListingPriceIncludingVat: null,
          listingPriceRoundingMethod: null,
          extraData: {},
        };
      }
    });
  });

  variations.forEach(variation => {
    if (existingConfig?.variations[variation.id]) {
      config.variations[variation.id] = existingConfig.variations[variation.id];
    } else {
      config.variations[variation.id] = {
        include: true,
        marginPercentOnBaseCost: null,
        targetListingPriceIncludingVat: null,
        listingPriceRoundingMethod: null,
        extraData: {},
      };
    }
  });

  return config;
}

// Checks that a configuration has all types, values and variations
export function checkListingVariationsConfigurationCompleteness<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>({
  configuration,
  types,
  variations,
  typeExtraDataCheck,
  valueExtraDataCheck,
  variationExtraDataCheck
}: {
  configuration: ExternalListingConfiguration;
  types: T[],
  variations: V[],
  typeExtraDataCheck: (
    listingConfig: ExternalListingConfiguration,
    typeConfig: VariationConfigurationType
  ) => {
    isValid: boolean;
    reason: string;
  },
  valueExtraDataCheck: (
    listingConfig: ExternalListingConfiguration,
    typeConfig: VariationConfigurationType,
    valueConfig: VariationConfigurationValue,
  ) => {
    isValid: boolean;
    reason: string;
  },
  variationExtraDataCheck: (
    listingConfig: ExternalListingConfiguration,
    variationConfig: VariationConfigurationVariation,
    typesConfig: VariationConfigurationType[],
    valuesConfig: VariationConfigurationValue[],
  ) => {
    isValid: boolean;
    reason: string;
  },
}) {
  // Check errors for types
  const typesErrors: {
    [typeId: string]: {
      typeId: string;
      typeName: string;
      isValid: boolean;
      reason: string;
    }
  } = {};

  types.forEach(type => {
    if (!configuration.variationsConfig.types[type.id]) {
      typesErrors[type.id] = {
        typeId: type.id,
        typeName: type.type,
        isValid: false,
        reason: `Type "${type.type}" not found in configuration`,
      };
    }

    const typeConfig = configuration.variationsConfig.types[type.id];

    const typeExtraDataError = typeExtraDataCheck(configuration, typeConfig);

    if (!typeExtraDataError.isValid) {
      typesErrors[type.id] = {
        typeId: type.id,
        typeName: type.type,
        isValid: false,
        reason: typeExtraDataError.reason,
      };
    }
  });

  // Check errors for values
  const valuesErrors: {
    [valueId: string]: {
      typeId: string;
      typeName: string;
      valueId: string;
      valueName: string;
      isValid: boolean;
      reason: string;
    }
  } = {};

  types.forEach(type => {
    const typeConfig = configuration.variationsConfig.types[type.id];

    type.typeValues.forEach(value => {
      if (!configuration.variationsConfig.values[value.id]) {
        valuesErrors[value.id] = {
          typeId: type.id,
          typeName: type.type,
          valueId: value.id,
          valueName: value.value,
          isValid: false,
          reason: `Value "${value.value}" for type "${type.type}" not found in configuration`,
        };
      }

      const valueConfig = configuration.variationsConfig.values[value.id];
      if (valueConfig?.include === false) {
        return;
      }

      const valueExtraDataError = valueExtraDataCheck(configuration, typeConfig, valueConfig);

      if (!valueExtraDataError.isValid) {
        valuesErrors[value.id] = {
          typeId: type.id,
          typeName: type.type,
          valueId: value.id,
          valueName: value.value,
          isValid: false,
          reason: valueExtraDataError.reason,
        };
      }
    });
  });

  // Check errors for variations

  const variationsErrors: {
    [variationId: string]: {
      variationId: string;
      variationName: string;
      isValid: boolean;
      reason: string;
    }
  } = {};

  variations.forEach(variation => {
    const typesConfig = variation.variationTypeValues.map(v => configuration.variationsConfig.types[v.type.id]);
    const valuesConfig = variation.variationTypeValues.map(v => configuration.variationsConfig.values[v.id]);

    const variationName = variation.variationTypeValues
      .map(v => `${v.type.type}: ${v.value}`)
      .join(', ');

    if (!configuration.variationsConfig.variations[variation.id]) {
      variationsErrors[variation.id] = {
        variationId: variation.id,
        variationName: variationName,
        isValid: false,
        reason: `Variation "${variationName}" not found in configuration`,
      };
    }

    const variationConfig = configuration.variationsConfig.variations[variation.id];
    const variationExtraDataError = variationExtraDataCheck(configuration, variationConfig, typesConfig, valuesConfig);

    if (!variationExtraDataError.isValid) {
      variationsErrors[variation.id] = {
        variationId: variation.id,
        variationName: variationName,
        isValid: false,
        reason: variationExtraDataError.reason,
      };
    }
  });

  const isValid = (
    Object.keys(typesErrors).length === 0
    && Object.keys(valuesErrors).length === 0
    && Object.keys(variationsErrors).length === 0
  );

  return { isValid, typesErrors, valuesErrors, variationsErrors };
}

// Wrapper for Leboncoin configuration
export function checkLeboncoinListingVariationsConfigurationCompleteness<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>({
  configuration,
  types,
  variations,
}: {
  configuration: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['configuration'];
  types: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['types'];
  variations: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['variations'];
}) {
  return checkListingVariationsConfigurationCompleteness({
    configuration,
    types,
    variations,
    typeExtraDataCheck: (listingConfig, typeConfig) => {
      if (
        (
          !listingConfig.useSingleVariationId
          && !typeConfig.useSingleValueId
        )
        && !typeConfig.extraData?.leboncoin_type_key
      ) {
        return { isValid: false, reason: 'Please select a leboncoin mapped type' };
      }

      if (listingConfig.useSingleVariationId && typeConfig.useSingleValueId
        && typeConfig.useSingleValueId !== listingConfig.useSingleVariationId) {
        return { isValid: false, reason: 'The selected variation is not the same as the selected single variation' };
      }

      return { isValid: true, reason: '' };
    },
    valueExtraDataCheck: (listingConfig, typeConfig, valueConfig) => {
      const mappedTypeKey = typeConfig.extraData?.leboncoin_type_key;
      const isSingleValue = typeConfig?.useSingleValueId;

      // if (
      //   !isSingleValue
      //   && !mappedTypeKey
      // ) {
      //   return { isValid: false, reason: 'Please select a leboncoin mapped type' };
      // }

      if (
        !listingConfig.useSingleVariationId
        && !isSingleValue
        && !valueConfig.extraData?.[mappedTypeKey]
      ) {
        return { isValid: false, reason: `Please select a leboncoin mapped value` };
      }

      return { isValid: true, reason: '' };
    },
    variationExtraDataCheck: (variationConfig) => {
      return { isValid: true, reason: '' };
    },
  });
}

// Wrapper for Ebay configuration
export function checkEbayListingVariationsConfigurationCompleteness<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>({
  configuration,
  types,
  variations,
}: {
  configuration: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['configuration'];
  types: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['types'];
  variations: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['variations'];
}) {
  return checkListingVariationsConfigurationCompleteness({
    configuration,
    types,
    variations,
    typeExtraDataCheck: (typeConfig) => {
      // TODO: map aspects instead of using same name
      return { isValid: true, reason: '' };
    },
    valueExtraDataCheck: (typeConfig, valueConfig) => {
      // TODO: map aspects instead of using same name
      return { isValid: true, reason: '' };
    },
    variationExtraDataCheck: (variationConfig) => {
      return { isValid: true, reason: '' };
    },
  });
}

// Wrapper for Temu configuration
export function checkTemuListingVariationsConfigurationCompleteness<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>({
  configuration,
  types,
  variations,
}: {
  configuration: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['configuration'];
  types: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['types'];
  variations: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['variations'];
}) {
  return checkListingVariationsConfigurationCompleteness({
    configuration,
    types,
    variations,
    typeExtraDataCheck: (typeConfig) => {
      return { isValid: true, reason: '' };
    },
    valueExtraDataCheck: (typeConfig, valueConfig) => {
      return { isValid: true, reason: '' };
    },
    variationExtraDataCheck: (configuration, variationConfig, typesConfig, valuesConfig) => {
      const isIncluded = variationConfig.include
        && typesConfig.every(t => t.useSingleValueId !== null)
        && valuesConfig.every(v => v.include);

      if (!variationConfig.externalId && isIncluded) {
        return { isValid: false, reason: 'Missing Temu SKU ID' };
      }

      return { isValid: true, reason: '' };
    },
  });
}

// Wrapper for Aliexpress configuration
export function checkAliexpressListingVariationsConfigurationCompleteness<
  V extends VariationRequiredData,
  T extends VariationTypeRequiredData,
>({
  configuration,
  types,
  variations,
}: {
  configuration: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['configuration'];
  types: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['types'];
  variations: Parameters<typeof checkListingVariationsConfigurationCompleteness<V, T>>[0]['variations'];
}) {
  return checkListingVariationsConfigurationCompleteness({
    configuration,
    types,
    variations,
    typeExtraDataCheck: (typeConfig) => {
      return { isValid: true, reason: '' };
    },
    valueExtraDataCheck: (typeConfig, valueConfig) => {
      return { isValid: true, reason: '' };
    },
    variationExtraDataCheck: (configuration, variationConfig, typesConfig, valuesConfig) => {
      const isIncluded = variationConfig.include
        && typesConfig.every(t => t.useSingleValueId !== null)
        && valuesConfig.every(v => v.include);

      if (!variationConfig.externalId && isIncluded) {
        return { isValid: false, reason: 'Missing Aliexpress SKU ID' };
      }

      return { isValid: true, reason: '' };
    },
  });
}

// Check if a type value is excluded due to parent type configuration
export function isValueExcludedByType(
  typeId: string,
  valueId: string,
  configuration: ExternalListingConfiguration,
) {
  const typeConfig = configuration.variationsConfig.types[typeId];
  return (
    (
      typeConfig?.useSingleValueId !== null
      && typeConfig.useSingleValueId !== valueId
    )
  );
}

// Check if a variation is excluded due to its type values
export function isVariationExcludedByTypeValues<
  V extends VariationRequiredData['variationTypeValues'][number],
>(
  typeValues: V[],
  configuration: ExternalListingConfiguration,
) {
  return typeValues.reduce<{
    valueExcluded: boolean;
    valuesExcluding: V[];
    typeExcluded: boolean;
    typesExcluding: V['type'][];
  }>((acc, tv) => {
    const valueConfig = configuration.variationsConfig.values[tv.id];
    const isValueExcluded = !(valueConfig?.include)
    const isTypeExcluded = isValueExcludedByType(tv.type.id, tv.id, configuration);
    return {
      valueExcluded: acc.valueExcluded || isValueExcluded,
      valuesExcluding: acc.valuesExcluding.concat(isValueExcluded ? [tv] : []),
      typeExcluded: acc.typeExcluded || isTypeExcluded,
      typesExcluding: acc.typesExcluding.concat(isTypeExcluded ? [tv.type] : []),
    };
  }, { valueExcluded: false, valuesExcluding: [], typeExcluded: false, typesExcluding: [] as V['type'][] });
}

// Check all possible reasons why a variation is excluded
export function isVariationExcluded<
  V extends VariationRequiredData,
>(
  variation: V,
  configuration: ExternalListingConfiguration,
) {
  const {
    valueExcluded,
    valuesExcluding,
    typeExcluded,
    typesExcluding,
  } = isVariationExcludedByTypeValues(
    variation.variationTypeValues,
    configuration,
  );

  const variationExcluded = (
    configuration.variationsConfig.variations[variation.id].include === false
    ||
    (
      configuration.useSingleVariationId
      && configuration.useSingleVariationId !== variation.id
    )
  );
  
  return {
    excluded: valueExcluded || typeExcluded || variationExcluded,
    variationExcluded,
    valueExcluded,
    valuesExcluding,
    typeExcluded,
    typesExcluding,
  };
}



// Apply configuration to variations list
export function filterVariationsByConfiguration<
  V extends VariationRequiredData,
>(
  variations: V[],
  configuration: ExternalListingConfiguration,
): V[] {
  return variations.filter(variation => {
    const { excluded } = isVariationExcluded(variation, configuration);
    return !excluded;
  });
}